import * as React from "react";
import {
  AlertTriangle,
  Check,
  FileText,
  Info,
  LucideIcon,
  Paperclip,
  Pencil,
  ReceiptText,
  X
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/Combobox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { CURRENCIES } from "@/lib/countries";
import { cn } from "@/lib/utils";
import { NetPayableLine } from "@/lib/advance";
import { billTotals, exceededByConverted } from "@/lib/bills";
import { expenseFileUrl, fetchExpenseCategories, requestDocumentFileUrl } from "../../api";
import {
  BillFields,
  CategoryLimits,
  ExpenseCategory,
  ExpenseLineItem,
  OTHER_CLAIM_TYPE_LABEL,
  OtherBillFieldValues,
  RequestDocument,
  ReviewEdit,
  TravelRequestSummary
} from "../../types";
import {
  OtherBillInputs,
  missingOtherBillField,
  otherBillDetails,
  otherBillPayload,
  otherBillValuesOf
} from "@/components/requests/OtherBillFields";
import { formatDate, formatDateTime } from "@/lib/datetime";

// Card-grouped section of a detail dialog - a titled header with an icon, then
// its body. Used for "Request details", "Rejection details", "Bills", etc. in
// RequestDetailDialog / AdvanceDetailDialog. `tone="destructive"` tints the
// header for a rejection block (see the reference design).
export function DetailCard({
  icon: Icon,
  title,
  tone = "default",
  children,
  className
}: {
  icon: LucideIcon;
  title: string;
  tone?: "default" | "destructive";
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("rounded-lg border bg-card", className)}>
      <header
        className={cn(
          "flex items-center gap-2 border-b px-4 py-2.5",
          tone === "destructive" && "border-destructive/20 bg-destructive/5"
        )}
      >
        <Icon
          className={cn(
            "h-4 w-4 shrink-0",
            tone === "destructive"
              ? "text-destructive"
              : "text-muted-foreground"
          )}
        />
        <h3
          className={cn(
            "text-sm font-semibold",
            tone === "destructive" && "text-destructive"
          )}
        >
          {title}
        </h3>
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

// A single labelled field with a leading icon - the person / briefcase / plane
// / globe / calendar / dollar rows inside a DetailCard. Lays out two per row in
// the parent's grid; pass a full-width field with className="col-span-2".
export function DetailField({
  icon: Icon,
  label,
  value,
  className
}: {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-start gap-2.5", className)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="break-words font-medium">{value}</dd>
      </div>
    </div>
  );
}

// Human labels for the raw EDITABLE_FIELDS keys a reviewEdit.fields array
// can contain (see db/requests_store.py / db/advances_store.py) - shared by
// RequestDetailDialog and AdvanceDetailDialog's own ReviewEditNote below.
// Falls back to the raw key itself for anything not listed, so a future
// editable field doesn't need this map updated before it displays at all.
const FIELD_LABEL: Record<string, string> = {
  department: "Department",
  country: "Country",
  travelType: "Travel type",
  originCountry: "Starting country",
  startCity: "Start city",
  endCity: "End city",
  departureAt: "Departure",
  arrivalAt: "Arrival",
  flightDetails: "Purpose of travel",
  currency: "Currency",
  currencyAutoSelected: "Currency auto-select",
  linkedAdvanceId: "Linked advance",
  amount: "Amount",
  reason: "Any Other Request",
  // Not a request-level field at all - a marker extra_review_fields adds
  // when an approver corrects a bill's amount/category/description during
  // Review (see requests_api.py's review_edit_expense), since a bill edit
  // has nothing in EDITABLE_FIELDS to diff against.
  bills: "bill details"
};

// Stage order a reviewEdit's entries are shown in - Manager's edit (if any)
// always reads before Finance's, matching the order those stages actually
// run in, regardless of insertion order in the object.
const REVIEW_STAGE_ORDER: Array<"Manager" | "Finance"> = ["Manager", "Finance"];
const REVIEW_STAGE_LABEL: Record<"Manager" | "Finance", string> = {
  Manager: "the reporting manager",
  Finance: "the Finance Approver"
};

// "Edited by X during review" note(s) - shown in a request/advance's detail
// dialog when an approver's Review action changed its fields (see
// ApprovalStore.request_review / AdvanceApprovalStore.review). Reads
// straight off the document's own reviewEdit field, not the audit log, so
// it's visible to the employee and anyone else who opens the dialog, not
// just an Admin querying audit_logs. reviewEdit is keyed by stage, so a
// Manager's Review and a later Finance Review both render their own card -
// one overwriting the other's entry would otherwise hide the Manager's edit
// the moment Finance reviews it too. Shown identically to every viewer
// (employee, Manager, Finance) - no stage is hidden from anyone.
export function ReviewEditNote({ reviewEdit }: { reviewEdit: ReviewEdit }) {
  const entries = REVIEW_STAGE_ORDER.map((stage) => ({
    stage,
    entry: reviewEdit[stage]
  })).filter(
    (x): x is { stage: "Manager" | "Finance"; entry: NonNullable<typeof x.entry> } =>
      !!x.entry && x.entry.fields.length > 0
  );
  if (entries.length === 0) {
    return null;
  }
  return (
    <>
      {entries.map(({ stage, entry }) => (
        <DetailCard
          key={stage}
          icon={Pencil}
          title={`Edited by ${REVIEW_STAGE_LABEL[stage]}`}
        >
          <p className="text-sm">
            <span className="font-medium">
              {entry.editedByName || "An approver"}
            </span>{" "}
            updated {entry.fields.map((f) => FIELD_LABEL[f] || f).join(", ")}{" "}
            before approving.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {formatDateTime(entry.editedAt)}
          </p>
        </DetailCard>
      ))}
    </>
  );
}

/** Who a net-payable line is about, worded for whoever is reading it - "you"
 *  for the requester themself, otherwise the requester's name. */
export function payeeLabel(
  request: Pick<TravelRequestSummary, "viewerIsRequester" | "requester">
): string {
  if (request.viewerIsRequester) return "you";
  return (
    request.requester?.displayName || request.requester?.email || "the employee"
  );
}

// "Net payable to …" / "Owed by …" lines under a Linked advance card, one per
// currency (see netPayableByCurrency in lib/advance.ts) - the app's own
// theme green (text-primary) where the employee is still owed money, red
// where the advance over-covered it and they owe the difference back.
// `party` is payeeLabel()'s wording.
export function NetPayableRows({
  lines,
  party
}: {
  lines: NetPayableLine[];
  party: string;
}) {
  if (lines.length === 0) return null;
  return (
    <div className="mt-2 space-y-1 border-t pt-2">
      {lines.map((line) => (
        <p
          key={line.currency}
          className={cn(
            "flex items-baseline justify-between text-sm font-semibold",
            line.direction === "payable"
              ? "text-primary"
              : "text-destructive"
          )}
        >
          <span className="font-normal text-muted-foreground">
            {line.direction === "payable"
              ? `Net payable to ${party} (${line.currency})`
              : `Owed by ${party} (${line.currency})`}
          </span>
          <span>
            {line.currency} {line.amount.toLocaleString()}
          </span>
        </p>
      ))}
    </div>
  );
}

export interface BillEditFields {
  category: ExpenseCategory;
  amount: number;
  currency: string;
  description: string;
  /** Other reimbursement bills only (see BillsSummaryCard's billFields). */
  other?: OtherBillFieldValues;
}

// Built once at module scope - same ALL_COUNTRY_OPTIONS pattern as
// TripLocationFields.tsx. Used by the bill-edit currency Combobox below
// (searchable, since the full CURRENCIES list is too long to scan in a
// plain dropdown).
const CURRENCY_OPTIONS = CURRENCIES.map((c) => ({ value: c, label: c }));

/** A travel bill's own fields not already shown inline on its row (filename,
 *  description, category and amount are) - date, vendor and when it was
 *  uploaded - behind the same tap-to-open "Bill details" popover an Other
 *  reimbursement bill gets via otherBillDetails. */
function travelBillDetails(expense: ExpenseLineItem): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = [];
  if (expense.date) out.push({ label: "Bill date", value: formatDate(expense.date) });
  if (expense.vendor) out.push({ label: "Vendor", value: expense.vendor });
  out.push({ label: "Category", value: expense.category });
  if (expense.description) out.push({ label: "Description", value: expense.description });
  out.push({ label: "Amount", value: `${expense.currency} ${expense.amount.toLocaleString()}` });
  out.push({ label: "Uploaded", value: formatDateTime(expense.uploadedAt) });
  return out;
}

// "Bills" card for a Reimbursement request - the uploaded/manually added
// expenses, a total PER currency present against the policy ceiling (only
// the request's own currency has a ceiling to compare against; any other
// currency present just gets its own plain sum, no exceeds-policy line),
// and any exceeds-policy flags. Shared by RequestDetailDialog (always
// read-only there - an approver/employee just viewing a request) and
// ReviewRequestDialog (an approver editing trip fields during Review) - a
// Reimbursement claim's actual bills are what's being approved, so Review
// showing only the trip fields and nothing of what was spent left an
// approver unable to see what they were signing off on.
//
// `onEditExpense`, when given, turns on a per-bill pencil button that opens
// an inline category/amount/currency/description edit (Save calls it with
// the new values; Cancel discards). Omitted entirely in RequestDetailDialog
// (plain view) and in ReviewAdvanceDialog's context (advances have no bills
// at all). Deliberately NOT a full bill editor - no add/delete, no receipt
// replacement, no vendor/date - those stay exactly as the employee entered
// them; Resubmit (send the whole request back to the employee) is still the
// path for anything bigger than a field-level correction.
export function BillsSummaryCard({
  requestId,
  expenses,
  currency,
  estimatedAllowanceAmount,
  limitsByCurrency,
  trackingOnly = false,
  onEditExpense,
  onEditingChange,
  noLimit = false,
  billFields = "travel"
}: {
  requestId: string;
  expenses: ExpenseLineItem[];
  currency: string;
  estimatedAllowanceAmount: number;
  /** The request's policyLimitsByCurrencyAtSubmission - used only to leave
   *  bills in "No limit" categories (null) out of the "Exceeds policy by"
   *  comparison. Omitted/null: every bill counts, as before. */
  limitsByCurrency?: Record<string, CategoryLimits> | null;
  /** Fixed Allowance: titles the card "Expenses" (not "Bills") and shows the
   *  "Expenses (tracking only)" caption - kept as the prop name for now
   *  (used widely by callers) even though bills are no longer tracking-only
   *  in the sense of not affecting payout: a Fixed Allowance employee is now
   *  paid the allowance AND reimbursed for what they actually spent
   *  (confirmed desired behaviour - see lib/settlementAmount.ts's own
   *  fixedAllowanceSettlement doc comment), so these bills DO add to the
   *  final Net payable figure shown elsewhere (CurrencyConversionCard). The
   *  total here is still compared against estimatedAllowanceAmount and
   *  flagged with "Exceeds the fixed allowance by …" when the employee's
   *  actual spend came in over it - kept exactly as-is (confirmed desired):
   *  that warning still flags spend over the policy's per-category ceiling,
   *  regardless of whether it's paid (see currencyTotals' own comment
   *  above). */
  trackingOnly?: boolean;
  onEditExpense?: (expenseId: string, fields: BillEditFields) => Promise<void> | void;
  /** Fired whenever a bill's inline edit form is open - ReviewRequestDialog
   *  uses this to disable its own Save (which approves the whole request)
   *  while an edit here is unsaved, same "nothing to click until it's
   *  resolved" gate NewRequest.tsx's wizard applies to its own bill edits.
   *  Without this, opening a bill's edit, changing a field, then clicking
   *  the dialog's Save silently discarded the correction and approved the
   *  request anyway - worse than the employee-facing version of this gap,
   *  since it affects what actually gets approved. */
  onEditingChange?: (editing: boolean) => void;
  /** An Other reimbursement - no policy limits, so no "Exceeds policy"
   *  line (its estimatedAllowanceAmount is 0, which would otherwise flag
   *  the whole total). */
  noLimit?: boolean;
  /** An Other reimbursement claim type shows its own bill fields on each
   *  row and in the edit form; "travel" (the default) is unchanged. */
  billFields?: BillFields;
}) {
  const otherType = billFields === "travel" ? null : billFields;
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editOther, setEditOther] = React.useState<OtherBillFieldValues>({});
  const [editCategory, setEditCategory] = React.useState<ExpenseCategory>("Other");
  const [editAmount, setEditAmount] = React.useState("");
  const [editCurrency, setEditCurrency] = React.useState("");
  const [editDescription, setEditDescription] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);
  // Admin-configured (Policies.tsx's "Expense Categories" panel) - fetched
  // only when an edit form can actually open (onEditExpense given), same
  // self-fetch convention ExpensesStep.tsx's own category dropdown uses
  // rather than threading this down from RequestDetailDialog/
  // ReviewRequestDialog (RequestDetailDialog never passes onEditExpense at
  // all, so it never needs this fetch to run).
  const [categories, setCategories] = React.useState<string[]>([]);
  React.useEffect(() => {
    if (!onEditExpense) return;
    fetchExpenseCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [onEditExpense]);

  React.useEffect(() => {
    onEditingChange?.(editingId !== null);
  }, [editingId, onEditingChange]);
  // Report "not editing" on unmount - e.g. the dialog is closed outright
  // with an edit form still open - so a stale `true` can't leave the
  // PARENT dialog's own Save disabled the next time it's opened.
  React.useEffect(() => {
    return () => onEditingChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = (expense: ExpenseLineItem) => {
    setEditingId(expense.id);
    setEditCategory(expense.category);
    setEditAmount(String(expense.amount));
    setEditCurrency(expense.currency || currency);
    setEditDescription(expense.description || "");
    setEditOther(otherBillValuesOf(expense));
    setEditError(null);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditError(null);
  };
  const saveEdit = async (expenseId: string) => {
    const amount = Number(editAmount);
    if (!editAmount.trim() || Number.isNaN(amount) || amount <= 0) {
      setEditError("Enter a valid amount greater than 0.");
      return;
    }
    if (!editCurrency.trim()) {
      setEditError("Select a currency.");
      return;
    }
    const missing = otherType ? missingOtherBillField(otherType, editOther) : null;
    if (missing) {
      setEditError(`Enter the ${missing} for this bill.`);
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      await onEditExpense?.(expenseId, {
        category: editCategory,
        amount,
        currency: editCurrency.trim().toUpperCase(),
        description: editDescription.trim(),
        ...(otherType ? { other: otherBillPayload(otherType, editOther) } : {})
      });
      setEditingId(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Could not save this bill.");
    } finally {
      setSaving(false);
    }
  };
  // Shown for Fixed Allowance (trackingOnly) too: these bills now add to
  // what's paid (see trackingOnly's own doc comment above), and spending
  // more than the allowance is still worth flagging to the approver, same
  // as Reimbursement bills exceeding their policy ceiling.
  const currencyTotals = React.useMemo(
    () =>
      billTotals(expenses, currency, estimatedAllowanceAmount, limitsByCurrency),
    [expenses, currency, estimatedAllowanceAmount, limitsByCurrency]
  );
  // currencyTotals.exceededBy only counts bills already in `currency` -
  // exceededBy, converted, also counts a bill in any OTHER currency,
  // converted via Frankfurter (confirmed desired behaviour - a client
  // requirement change, same server-side treatment
  // requests_api.py's _category_limit now gives every bill). Computed in an
  // effect (live FX calls), defaulting to the unconverted figure until it
  // resolves so the warning doesn't flicker from "nothing" to a number.
  const [exceededBy, setExceededBy] = React.useState(currencyTotals.exceededBy);
  React.useEffect(() => {
    let cancelled = false;
    exceededByConverted(expenses, currency, estimatedAllowanceAmount, limitsByCurrency)
      .then((value) => !cancelled && setExceededBy(value))
      .catch(() => !cancelled && setExceededBy(currencyTotals.exceededBy));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses, currency, estimatedAllowanceAmount, limitsByCurrency]);

  return (
    <DetailCard
      icon={ReceiptText}
      title={
        trackingOnly
          ? `Expenses (${expenses.length})`
          : `Bills (${expenses.length})`
      }
    >
      {trackingOnly && (
        <p className="mb-3 text-xs text-muted-foreground">
          These add to the fixed allowance paid for this trip.
        </p>
      )}
      {(currencyTotals.totalInRequestCurrency > 0 || currencyTotals.others.length > 0) && (
        <div className="mb-3 space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">
              Total: {currency} {currencyTotals.totalInRequestCurrency.toLocaleString()}
            </p>
            {exceededBy > 0 && !noLimit && (
              <p className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                {trackingOnly
                  ? `Exceeds the fixed allowance by ${currency} ${exceededBy.toLocaleString()}`
                  : `Exceeds policy by ${currency} ${exceededBy.toLocaleString()}`}
              </p>
            )}
          </div>
          {currencyTotals.others.map((o) => (
            <p key={o.currency} className="text-xs text-muted-foreground">
              Total ({o.currency}): {o.currency} {o.total.toLocaleString()}
            </p>
          ))}
        </div>
      )}
      <div className="space-y-2">
        {expenses.map((expense) =>
          onEditExpense && editingId === expense.id ? (
            <div
              key={expense.id}
              className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-2.5 text-xs"
            >
              <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,0.8fr)_minmax(0,1fr)] gap-2">
                <Select
                  value={editCategory}
                  onValueChange={(v) => setEditCategory(v as ExpenseCategory)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  className="h-8 text-xs"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  placeholder="Amount"
                />
                <Combobox
                  value={editCurrency}
                  onChange={setEditCurrency}
                  options={CURRENCY_OPTIONS}
                  placeholder="Currency"
                  searchPlaceholder="Search…"
                  className="h-8 text-xs"
                  contentClassName="w-40"
                />
              </div>
              {otherType && (
                <div className="grid gap-2 sm:grid-cols-2">
                  <OtherBillInputs
                    claimType={otherType}
                    value={editOther}
                    onChange={(patch) => setEditOther((prev) => ({ ...prev, ...patch }))}
                    idPrefix={`review-${expense.id}`}
                  />
                </div>
              )}
              {/* A City travel bill has its own Purpose instead. */}
              {otherType !== "city_travel" && (
                <Input
                  className="h-8 text-xs"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Description (optional)"
                />
              )}
              {editError && <p className="text-destructive">{editError}</p>}
              <div className="flex justify-end gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  onClick={cancelEdit}
                  disabled={saving}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() => saveEdit(expense.id)}
                  disabled={saving}
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <div
              key={expense.id}
              // Phones: filename+details spans the full row 1; category
              // badge (row 2 col 1) and amount (row 2 col 2) share row 2;
              // edit button spans its own row 3 (explicit grid-row/
              // grid-column placement - CSS order alone doesn't reliably
              // move an item INTO a specific cell, only its paint/tab
              // sequence, so each piece gets an explicit cell instead).
              // Three fixed columns from sm up (four when editable, for the
              // trailing pencil button), where grid-row/col resets to auto.
              className={cn(
                "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-md border p-2.5 text-xs",
                onEditExpense
                  ? "sm:grid-cols-[minmax(0,1fr)_7.5rem_5.5rem_1.75rem]"
                  : "sm:grid-cols-[minmax(0,1fr)_7.5rem_5.5rem]"
              )}
            >
              <div className="[grid-area:1/1/1/3] flex min-w-0 items-center gap-2 sm:[grid-area:auto]">
                <div className="min-w-0 flex-1">
                  {expense.filename ? (
                    <a
                      href={expenseFileUrl(requestId, expense.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="block truncate font-medium text-primary underline"
                    >
                      {expense.filename}
                    </a>
                  ) : (
                    <span className="block truncate font-medium text-muted-foreground">
                      No receipt attached
                    </span>
                  )}
                  {expense.description && (
                    <p className="truncate text-muted-foreground">
                      {expense.description}
                    </p>
                  )}
                </div>
                {otherType && otherBillDetails(expense).length > 0 && (
                  // Same tap-to-open pattern as the "exceeds policy" icon
                  // below - works the same way on mouse and touch input.
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex shrink-0 items-center gap-1 whitespace-nowrap font-normal text-muted-foreground hover:text-primary"
                        aria-label={`View ${OTHER_CLAIM_TYPE_LABEL[otherType]} details`}
                      >
                        {OTHER_CLAIM_TYPE_LABEL[otherType]} details
                        <Info className="h-3 w-3 shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-3 text-xs" side="top" align="start">
                      <dl className="space-y-1.5">
                        {otherBillDetails(expense).map(({ label, value }) => (
                          <div key={label}>
                            <dt className="text-muted-foreground">{label}</dt>
                            <dd className="font-medium">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </PopoverContent>
                  </Popover>
                )}
                {!otherType && travelBillDetails(expense).length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex shrink-0 items-center gap-1 whitespace-nowrap font-normal text-muted-foreground hover:text-primary"
                        aria-label="View bill details"
                      >
                        Bill details
                        <Info className="h-3 w-3 shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-3 text-xs" side="top" align="start">
                      <dl className="space-y-1.5">
                        {travelBillDetails(expense).map(({ label, value }) => (
                          <div key={label}>
                            <dt className="text-muted-foreground">{label}</dt>
                            <dd className="whitespace-pre-wrap font-medium">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              <Badge
                variant="outline"
                className="[grid-area:2/1] w-fit min-w-0 max-w-full justify-self-start font-normal sm:[grid-area:auto] sm:justify-self-auto"
                title={expense.category}
              >
                <span className="min-w-0 truncate">{expense.category}</span>
              </Badge>
              <span className="[grid-area:2/2] flex items-center justify-end gap-1 whitespace-nowrap text-right font-medium tabular-nums sm:[grid-area:auto]">
                {!!expense.exceedsPolicyBy && (
                  // A native SVG <title> only shows on hover, which does
                  // nothing on a touchscreen - a tap-to-open Popover works the
                  // same way on both mouse and touch input.
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="shrink-0"
                        aria-label={`${expense.category} bills exceed the policy limit by ${currency} ${expense.exceedsPolicyBy.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                      >
                        <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                      </button>
                    </PopoverTrigger>
                    {/* The overage is for the whole category, in the trip's
                        own currency (every bill is converted to it first -
                        requests_api.py's _recompute_exceeds_policy), not this
                        bill's currency. */}
                    <PopoverContent className="max-w-xs p-2 text-xs" side="top">
                      <p>
                        {expense.category} bills exceed the policy limit by{" "}
                        <span className="font-medium">
                          {currency}{" "}
                          {expense.exceedsPolicyBy.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </span>
                      </p>
                      <p className="mt-1 text-muted-foreground">
                        All {expense.category} bills on this trip are added up
                        {(expense.currency || currency) !== currency &&
                          `, converted to ${currency} at each bill's date,`}{" "}
                        and checked against the {expense.category} limit for the
                        whole trip.
                      </p>
                    </PopoverContent>
                  </Popover>
                )}
                {expense.currency || currency} {expense.amount.toLocaleString()}
              </span>
              {onEditExpense && (
                <button
                  type="button"
                  className="[grid-area:3/1/3/3] justify-self-end flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground sm:[grid-area:auto] sm:justify-self-auto"
                  aria-label="Edit this bill"
                  onClick={() => startEdit(expense)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )
        )}
        {expenses.length === 0 && (
          <p className="py-3 text-center text-muted-foreground">
            No bills uploaded yet.
          </p>
        )}
      </div>
    </DetailCard>
  );
}

// Plain supporting attachments (visa, boarding pass, itinerary, etc.) - view
// links only, no amounts/categories. Rendered in RequestDetailDialog below
// BillsSummaryCard; the caller omits it entirely when there are none.
export function DocumentsSummaryCard({
  requestId,
  documents
}: {
  requestId: string;
  documents: RequestDocument[];
}) {
  return (
    <DetailCard icon={Paperclip} title="Additional documents">
      <ul className="space-y-2 text-sm">
        {documents.map((document) => (
          <li key={document.id} className="flex items-start gap-2.5">
            <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <a
                href={requestDocumentFileUrl(requestId, document.id)}
                target="_blank"
                rel="noreferrer"
                title={document.filename || undefined}
                className="block truncate font-medium text-primary underline"
              >
                {document.filename || "Document"}
              </a>
              {document.note && (
                <p className="break-words text-xs text-muted-foreground">
                  {document.note}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </DetailCard>
  );
}
